import React, { type FC, memo, use, useCallback } from "react";
import { type Key, Label, Tabs } from "@heroui/react";
import { Display, Moon, Sun } from "@gravity-ui/icons";
import { FormattedMessage, useIntl } from "react-intl";

import { type Theme, ThemeContext } from "@domain/context/ThemeProvider";

export const THEME_OPTIONS = [
    { value: "system", labelId: "app.page.profile.theme.system", icon: Display },
    { value: "light", labelId: "app.page.profile.theme.light", icon: Sun },
    { value: "dark", labelId: "app.page.profile.theme.dark", icon: Moon },
] as const satisfies readonly { value: Theme; labelId: string; icon: FC<{ className?: string }> }[];

const ThemeToggleTabs: FC = () => {
    const { theme, setTheme } = use(ThemeContext);

    const onThemeChange = useCallback((key: Key) => setTheme(key as Theme), [setTheme]);

    return (
        <div className="w-80">
            <Label className="mb-2">
                <FormattedMessage id="app.page.profile.theme" />
            </Label>
            <Tabs selectedKey={theme} onSelectionChange={onThemeChange}>
                <Tabs.ListContainer>
                    <Tabs.List aria-label="Theme">
                        {THEME_OPTIONS.map((opt) => (
                            <Tabs.Tab key={opt.value} id={opt.value}>
                                <opt.icon className="size-4" />
                                <FormattedMessage id={opt.labelId} />
                                <Tabs.Indicator />
                            </Tabs.Tab>
                        ))}
                    </Tabs.List>
                </Tabs.ListContainer>
            </Tabs>
        </div>
    );
};

// Tabs rather than ToggleButtonGroup: same widget as the labelled variant above, and it renders
// the segmented control this spot needs. (pdf.js's viewer CSS also defines `.toggle-button`; it no
// longer wins the cascade - see src/ui-components/PdfViewer/pdfjsViewer.css - but Tabs stays.)
const ThemeToggleCompact: FC = () => {
    const { theme, setTheme } = use(ThemeContext);
    const intl = useIntl();

    const onThemeChange = useCallback((key: Key) => setTheme(key as Theme), [setTheme]);

    return (
        <Tabs className="w-full" selectedKey={theme} onSelectionChange={onThemeChange}>
            <Tabs.ListContainer>
                <Tabs.List aria-label={intl.formatMessage({ id: "app.page.profile.theme" })} className="w-full">
                    {THEME_OPTIONS.map((opt) => (
                        <Tabs.Tab
                            key={opt.value}
                            id={opt.value}
                            className="flex-1 justify-center"
                            aria-label={intl.formatMessage({ id: opt.labelId })}
                            data-testid={`theme-toggle-${opt.value}`}
                        >
                            <opt.icon className="size-4" />
                            <Tabs.Indicator />
                        </Tabs.Tab>
                    ))}
                </Tabs.List>
            </Tabs.ListContainer>
        </Tabs>
    );
};

// `compact`: icon-only button group for tight spots (header dropdown).
// Default: labelled tabs with a field label, for settings pages.
const ThemeToggleImpl: FC<{ compact?: boolean }> = ({ compact }) =>
    compact ? <ThemeToggleCompact /> : <ThemeToggleTabs />;

export const ThemeToggle = memo(ThemeToggleImpl);
